import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@textile-erp/ui';
import { useMenuAccess } from '../providers/MenuAccessProvider';

interface RouteGuardProps {
  children: React.ReactNode;
}

export function RouteGuard({ children }: RouteGuardProps) {
  const { hasAccess, isLoading, accessiblePaths } = useMenuAccess();
  const { showError } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const toastShownRef = useRef<string | null>(null);
  // The path whose access was last checked. A paths reload while staying on that path keeps the
  // page mounted — swapping it for the spinner would wipe whatever the user was typing.
  const checkedPathRef = useRef<string | null>(null);

  // Check access whenever path or accessible paths change
  useEffect(() => {
    // Wait for paths to load
    if (isLoading) {
      console.log('🔄 RouteGuard: Still loading accessible paths...');
      if (checkedPathRef.current !== currentPath) setAccessChecked(false);
      return;
    }

    // Check access
    const canAccess = hasAccess(currentPath);
    console.log(`🔒 RouteGuard: Checking access to "${currentPath}" -> ${canAccess ? '✅ ALLOWED' : '🚫 DENIED'}`);
    console.log('📋 RouteGuard: Accessible paths:', accessiblePaths);

    setHasPermission(canAccess);
    setAccessChecked(true);
    checkedPathRef.current = currentPath;

    // Redirect if no access
    if (!canAccess) {
      console.warn(`🚫 RouteGuard: Redirecting from "${currentPath}" to dashboard`);

      // Show toast only once per denied path (avoid duplicate toasts)
      if (toastShownRef.current !== currentPath) {
        toastShownRef.current = currentPath;
        showError(
          'Access Denied',
          "You don't have permission to access this page."
        );
      }

      navigate('/', { replace: true });
    } else {
      // Reset toast ref when access is granted
      toastShownRef.current = null;
    }
  }, [currentPath, hasAccess, isLoading, navigate, accessiblePaths, showError]);

  // Show loading state while checking access
  if (!accessChecked || (isLoading && checkedPathRef.current !== currentPath)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Check if user has access
  if (!hasPermission) {
    // Show access denied briefly before redirect
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Redirecting to dashboard...
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
